import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Typography, Statistic } from "antd";
import { useAppDispatch, useAppSelector } from "@Components/CustomerApp/hooks";
import {
    promptCustomerToContinueChatVisibilityChanged,
    chatTimedOut,
    endedChat,
    viewStateChanged,
} from "../customerAppSlice";
import "./ContinueChatTimeoutModal.less";
import { ExtendChatSessionRequest } from "../../../api/dtos/chat/ExtendChatSessionRequest";
import { ChatState } from "../../../models/chat/ChatState";
import { PersistedMessageType } from "../../../hubs/chat/dtos/PersistedMessageType";
import { ClientRole } from "../../../hubs/chat/dtos/ClientRole";
import { TextMessage } from "../../../hubs/chat/dtos/TextMessage";
import { playReceivedMessageSound } from "@Utilities/SoundNotifications";
import { ViewState } from "../viewState";

const { Countdown } = Statistic;
const { Title, Text } = Typography;

const ContinueChatTimeoutModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const chatApi = useAppSelector((state) => state.customerApp.chatApi);
    const messageApi = useAppSelector((state) => state.customerApp.messageApi);
    const isVisible = useAppSelector(
        (state) =>
            state.customerApp.chatTimeoutContinuationModalSettings
                .isPromptCustomerToContinueChatVisible
    );
    const initialCountdown = useAppSelector(
        (state) =>
            state.customerApp.chatTimeoutContinuationModalSettings
                .countDownToUseInSeconds ?? 0
    );
    const [countdown, setCountdown] = useState<number>(0);
    const chatId = useAppSelector((state) => state.customerApp.chat?.id ?? "");
    const isBot = useAppSelector(
        (state) =>
            state.customerApp.chat?.state == ChatState.TalkingToBot ||
            state.customerApp.chat?.state == ChatState.ParkedWithBot
    );
    const continueButtonRef = useRef<HTMLButtonElement>(null);
    const [deadline, setDeadline] = useState<number>(0);
    const criticalCountdownThreshold = 20;
    const customerExtendedChatTimeoutMessage =
        "Customer extended chat timeout.";
    const promptMessage =
        "We’ll end the chat in 2 minutes if we don’t hear from you. Would you like to keep chatting?";

    // Using deadline for a stable state var for the antd Countdown component to retain its ticking down value across re-renders.
    useEffect(() => {
        if (isVisible && initialCountdown > 0) {
            setCountdown(initialCountdown); // Initializing here so we don't get the countdown--critical class styling applied for a brief moment when modal first becomes visible.
            setDeadline(Date.now() + initialCountdown * 1000);
            playReceivedMessageSound();
        }
    }, [isVisible, initialCountdown]);

    // Using the countDown state var as the trigger for the UI "critical" styling of the antd Countdown component.
    useEffect(() => {
        if (!deadline) return;

        const interval = setInterval(() => {
            const remaining = Math.max(
                0,
                Math.round((deadline - Date.now()) / 1000)
            );
            setCountdown(remaining);
        }, 1000);

        return () => clearInterval(interval);
    }, [deadline]);

    useEffect(() => {
        if (isVisible) {
            // Delay focus long enough for modal and content to mount.
            const timer = setTimeout(() => {
                continueButtonRef.current?.focus();
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [isVisible]);

    // Announce modal content for accessibility.
    useEffect(() => {
        if (isVisible) {
            const liveRegion = document.getElementById(
                "continue-chat-modal-live-region"
            );
            if (liveRegion) {
                liveRegion.textContent = promptMessage;
            }
        }
    }, [isVisible]);

    const handleTimeoutChat = (): void => {
        dispatch(
            promptCustomerToContinueChatVisibilityChanged({
                chatId: chatId,
                countDownToUseInSeconds: 0,
            })
        );
        dispatch(chatTimedOut({ isBot: isBot }));
    };

    const handleEndChat = (): void => {
        dispatch(
            promptCustomerToContinueChatVisibilityChanged({
                chatId: chatId,
                countDownToUseInSeconds: 0,
            })
        );

        dispatch(endedChat());

        chatApi.endChat().catch(() => {
            dispatch(viewStateChanged(ViewState.Error));
        });
    };

    const handleContinueChat = async (): Promise<void> => {
        // Reset timer to hide modal.
        dispatch(
            promptCustomerToContinueChatVisibilityChanged({
                chatId: chatId,
                countDownToUseInSeconds: 0,
            })
        );

        await chatApi.extendChatSessionAboutToTimeout({
            chatId: chatId,
        } as ExtendChatSessionRequest);

        // This will be an event type message that will only show in operator view, if live chat, and in trancript.
        const messageToSend = {
            chatId: chatId,
            clientRole: ClientRole.Operator,
            messageType: PersistedMessageType.ChatSessionExtended,
            text: customerExtendedChatTimeoutMessage,
        } as TextMessage;
        messageApi?.sendChatSessionExtendedMessage(messageToSend);
    };

    return (
        <>
            <div
                className="continue-chat-modal__live-region"
                id="continue-chat-modal-live-region"
                aria-live="assertive"
            />
            <Modal
                className="continue-chat-modal"
                open={!!isVisible}
                title={null}
                closable={false}
                maskClosable={false}
                centered
                destroyOnClose
                aria-labelledby="continue-chat-modal-title"
                aria-describedby="continue-chat-modal-desc"
                afterClose={(): void => setCountdown(0)}
                width={420}
                bodyStyle={{
                    paddingTop: 10,
                    paddingBottom: 10,
                    position: "relative",
                }}
                footer={[
                    <Button
                        className="continue-chat-modal-footer__button-outlined"
                        key="endChat"
                        onClick={handleEndChat}
                        data-testid="continue-chat-modal-endchat-button"
                    >
                        End Chat
                    </Button>,
                    <Button
                        key="continueChat"
                        type="primary"
                        onClick={handleContinueChat}
                        ref={continueButtonRef}
                        data-testid="continue-chat-modal-continuechat-button"
                        id="continueChatButton"
                    >
                        Continue Chat
                    </Button>,
                ]}
            >
                <div className="continue-chat-modal__header">
                    <Title
                        className="continue-chat-modal__header__title"
                        id="continue-chat-modal-title"
                        level={4}
                    >
                        Still there?
                    </Title>
                    <div className="continue-chat-modal__header__count-section">
                        <span
                            role="timer"
                            aria-live="polite"
                            aria-label="Time remaining to continue the chat"
                        >
                            <Countdown
                                className={`continue-chat-modal__header__count-section__countdown ${
                                    countdown <= criticalCountdownThreshold
                                        ? "countdown--critical"
                                        : ""
                                }`}
                                value={deadline}
                                onFinish={handleTimeoutChat}
                                format="mm:ss"
                            />
                        </span>
                    </div>
                </div>
                <Text
                    className="continue-chat-modal__bodyText"
                    id="continue-chat-modal-desc"
                >
                    {promptMessage}
                </Text>
            </Modal>
        </>
    );
};

export default ContinueChatTimeoutModal;
